import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Upload, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import PageHelp from '../components/help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_TYPES = [
  ['LINK', 'Payment link'],
  ['VENMO', 'Venmo'],
  ['CASHAPP', 'Cash App'],
  ['ZELLE', 'Zelle'],
  ['PAYPAL', 'PayPal'],
  ['BANK', 'Bank transfer'],
  ['CHECK', 'Check / cash'],
  ['CUSTOM', 'Other'],
];

export default function InvoiceSettingsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    businessName: '',
    businessLogoPath: null,
    providerName: '',
    businessPhone: '',
    businessEmail: '',
    businessAddress: '',
    defaultInvoiceNote: '',
    paymentOptions: [],
  });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/invoice-settings`, { headers });
      setSettings({ paymentOptions: [], ...data });
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await axios.post(
        `${API_URL}/api/dashboard/invoice-settings/logo`,
        form,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setSettings((p) => ({ ...p, businessLogoPath: data.path }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const addOption = () => {
    setSettings((p) => ({
      ...p,
      paymentOptions: [
        ...(p.paymentOptions || []),
        { id: Math.random().toString(36).slice(2), enabled: true, label: '', type: 'CUSTOM', value: '', instructions: '' },
      ],
    }));
  };

  const updateOption = (idx, patch) => {
    setSettings((p) => ({
      ...p,
      paymentOptions: p.paymentOptions.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  };
  const removeOption = (idx) =>
    setSettings((p) => ({ ...p, paymentOptions: p.paymentOptions.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        businessName: settings.businessName || null,
        businessLogoPath: settings.businessLogoPath || null,
        providerName: settings.providerName || null,
        businessPhone: settings.businessPhone || null,
        businessEmail: settings.businessEmail || null,
        businessAddress: settings.businessAddress || null,
        defaultInvoiceNote: settings.defaultInvoiceNote || null,
        paymentOptions: (settings.paymentOptions || []).map((o) => ({
          id: o.id, enabled: !!o.enabled, label: o.label || 'Payment',
          type: o.type || 'CUSTOM',
          value: o.value || null,
          instructions: o.instructions || null,
        })),
      };
      await axios.put(`${API_URL}/api/dashboard/invoice-settings`, payload, { headers });
      toast.success('Invoice settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const logoUrl = settings.businessLogoPath
    ? `${API_URL}/api/files/logos/${settings.businessLogoPath.replace(/^ranchworks\/logos\//, '')}`
    : null;

  if (loading) {
    return <DashboardLayout><div className="p-6"><Loader2 className="h-6 w-6 animate-spin" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto p-4 space-y-4" data-testid="invoice-settings-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <h1 className="text-2xl font-bold">Invoice Settings</h1>
            <PageHelp
              testId="invoice-settings"
              title="Invoice Settings"
              purpose="Set up how your invoices look and how clients pay you."
              actions={[
                { label: 'Upload logo', desc: 'add your business logo to every invoice' },
                { label: 'Add option', desc: 'add a way to get paid — Venmo, Zelle, check, etc.' },
                { label: 'Save', desc: 'save your changes' },
              ]}
              nextStep="Add at least one payment option so clients know how to pay."
            />
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-invoice-settings-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <Save className="h-4 w-4 mr-1" /> Save
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>Business Info</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {/* Logo */}
            <div className="space-y-1">
              <Label>Business logo</Label>
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-16 w-16 object-contain border rounded" data-testid="logo-preview" />
                ) : (
                  <div className="h-16 w-16 border rounded flex items-center justify-center text-xs text-muted-foreground">No logo</div>
                )}
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-muted">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Upload
                  </div>
                  <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-upload-input" />
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">PNG / JPG / SVG up to 5MB.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Business name</Label>
                <Input value={settings.businessName || ''} onChange={(e) => setSettings({ ...settings, businessName: e.target.value })} data-testid="business-name-input" /></div>
              <div className="space-y-1"><Label>Provider name</Label>
                <Input value={settings.providerName || ''} onChange={(e) => setSettings({ ...settings, providerName: e.target.value })} data-testid="provider-name-input" /></div>
              <div className="space-y-1"><Label>Business phone</Label>
                <Input value={settings.businessPhone || ''} onChange={(e) => setSettings({ ...settings, businessPhone: e.target.value })} data-testid="business-phone-input" /></div>
              <div className="space-y-1"><Label>Business email</Label>
                <Input type="email" value={settings.businessEmail || ''} onChange={(e) => setSettings({ ...settings, businessEmail: e.target.value })} data-testid="business-email-input" /></div>
              <div className="space-y-1 sm:col-span-2"><Label>Business address</Label>
                <Input value={settings.businessAddress || ''} onChange={(e) => setSettings({ ...settings, businessAddress: e.target.value })} data-testid="business-address-input" /></div>
              <div className="space-y-1 sm:col-span-2"><Label>Default invoice note</Label>
                <Textarea rows={2} value={settings.defaultInvoiceNote || ''} onChange={(e) => setSettings({ ...settings, defaultInvoiceNote: e.target.value })} data-testid="default-invoice-note-input" placeholder="Thanks for your business!" /></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Payment Options</CardTitle>
            <Button size="sm" variant="outline" onClick={addOption} data-testid="add-payment-option-btn">
              <Plus className="h-4 w-4 mr-1" /> Add option
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {(!settings.paymentOptions || !settings.paymentOptions.length) && (
              <p className="text-sm text-muted-foreground">Add a payment method (Venmo, Zelle, check, bank, etc.). It will appear on every invoice unless disabled.</p>
            )}
            {(settings.paymentOptions || []).map((o, idx) => (
              <div key={o.id} className="border rounded p-3 space-y-2" data-testid={`payment-option-row-${idx}`}>
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={!!o.enabled}
                    onCheckedChange={(v) => updateOption(idx, { enabled: !!v })}
                    data-testid={`payment-option-enabled-${idx}`}
                  />
                  <Input
                    placeholder="Display label (e.g. Venmo)"
                    value={o.label}
                    onChange={(e) => updateOption(idx, { label: e.target.value })}
                    className="max-w-[200px]"
                    data-testid={`payment-option-label-${idx}`}
                  />
                  <Select value={o.type || 'CUSTOM'} onValueChange={(v) => updateOption(idx, { type: v })}>
                    <SelectTrigger className="max-w-[160px]" data-testid={`payment-option-type-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => removeOption(idx)} data-testid={`payment-option-remove-${idx}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
                <Input
                  placeholder="Link, handle, phone, email, or account number"
                  value={o.value || ''}
                  onChange={(e) => updateOption(idx, { value: e.target.value })}
                  data-testid={`payment-option-value-${idx}`}
                />
                <Textarea
                  placeholder="Written instructions (optional)"
                  rows={2}
                  value={o.instructions || ''}
                  onChange={(e) => updateOption(idx, { instructions: e.target.value })}
                  data-testid={`payment-option-instructions-${idx}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <Save className="h-4 w-4 mr-1" /> Save settings
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
